import {
  type FC, useCallback, useState,
} from 'react';
import {
  ExportDataModalComponent, type ExportDataModalResults,
} from '~/_shared/components/exportDataModal/exportDataModal.component';
import { prepareAsyncCopyToClipboard } from '~/_shared/utils/clipboard/clipboard.helpers';
import { downloadFile } from '~/_shared/utils/document/document.helpers';
import { useTranslation } from '~/_shared/utils/hooks';
import { useTimeout } from '~/_shared/utils/hooks/useTimeout';
import { ExportDataMethod } from '~/data/exportDataModal/exportDataMethod.enum';
import { type ModalProps } from '~/modal/modalType.enum';
import { useModal } from '~/modal/useModal.hook';
import { useGetBoundaryTerritoryAssignments } from '../hooks/useGetBoundaryTerritoryAssignments';

export type ExportBoundaryTerritoryAssignmentsContainerProps = ModalProps<{
  boundaryTerritoryGroupId: number;
}>;

export const ExportBoundaryTerritoryAssignmentsContainer: FC<ExportBoundaryTerritoryAssignmentsContainerProps> = ({
  boundaryTerritoryGroupId,
  isOpen,
  onClose,
}) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isCopiedToClipboard, showCopiedToClipboardMessage] = useTimeout(3000);
  const { openModal: openDownloadResultsModal } = useModal('DownloadResults');
  const {
    exportBoundaryTerritoryGroupAssignmentsAsFile,
    exportBoundaryTerritoryGroupAssignmentsAsString,
    isLoading,
  } = useGetBoundaryTerritoryAssignments();
  const [t] = useTranslation();

  const onSubmit = useCallback(async (results: ExportDataModalResults) => {
    // the clipboard has to be prepared in the synchronous part of the click handler because of Safari
    const asyncCopyToClipboard = prepareAsyncCopyToClipboard();
    setErrorMessage(null);

    if (results.method === ExportDataMethod.Clipboard) {
      const assignmentsCsv = await exportBoundaryTerritoryGroupAssignmentsAsString({
        boundaryTerritoryGroupId,
      });

      if (!assignmentsCsv) {
        setErrorMessage(t('Error exporting data'));
        return;
      }

      asyncCopyToClipboard(assignmentsCsv);
      showCopiedToClipboardMessage();
      return;
    }

    const downloadUrl = await exportBoundaryTerritoryGroupAssignmentsAsFile({
      boundaryTerritoryGroupId,
      fileType: results.fileType,
    });

    if (!downloadUrl) {
      setErrorMessage(t('Error exporting data'));
      return;
    }

    downloadFile(downloadUrl);

    onClose();
    openDownloadResultsModal({
      downloadUrl,
    });
  }, [boundaryTerritoryGroupId, exportBoundaryTerritoryGroupAssignmentsAsFile, exportBoundaryTerritoryGroupAssignmentsAsString, onClose, openDownloadResultsModal, showCopiedToClipboardMessage, t]);

  return (
    <ExportDataModalComponent
      isLoading={isLoading}
      onSubmit={onSubmit}
      onClose={onClose}
      isOpen={isOpen}
      error={errorMessage}
      showCopiedToClipboardTooltip={isCopiedToClipboard}
      noticeTrialUsers
    />
  );
};
